import { useMemo, useRef } from 'react';
import type { MasterGraph, StemGraph } from './graphBuilder';

export function useEngineRefs() {
  const audioCtxRef = useRef<AudioContext | null>(null);

  // Master chain refs
  const masterGainRef = useRef<GainNode | null>(null);
  // Dedicated transport gate (sits after masterGain, before EQ)
  const gateGainRef = useRef<GainNode | null>(null);
  const masterAnalyserPreRef = useRef<AnalyserNode | null>(null);
  const masterAnalyserRef = useRef<AnalyserNode | null>(null);
  const masterFilterRef = useRef<BiquadFilterNode | null>(null);
  const masterHighpassRef = useRef<BiquadFilterNode | null>(null);
  const masterLimiterRef = useRef<DynamicsCompressorNode | null>(null);
  const masterClipperRef = useRef<WaveShaperNode | null>(null);
  const masterLookaheadLimiterRef = useRef<AudioWorkletNode | null>(null);
  // Passive tee for scopes/meters (pre-gate)
  const teeGainRef = useRef<GainNode | null>(null);

  // Stem chain refs
  const sourcesRef = useRef<Record<string, MediaElementAudioSourceNode>>({});
  const gainsRef = useRef<Record<string, GainNode>>({});
  const analyzersRef = useRef<Record<string, AnalyserNode>>({});
  const delaysRef = useRef<Record<string, DelayNode>>({});
  const connectedElsRef = useRef<Record<string, HTMLAudioElement | null>>({});
  const elsRef = useRef<Record<string, HTMLAudioElement | null>>({});
  const durationsRef = useRef<Record<string, number>>({});
  const lastNonZeroVolRef = useRef<Record<string, number>>({});

  // 🔥 New high-level fields for RB integration (mutable object fields stored on the container)
  // Keep these as properties on the returned object; they will be stable via useMemo container.

  return useMemo(() => ({
    audioCtxRef,
    masterGainRef,
    gateGainRef,
    masterAnalyserPreRef,
    masterAnalyserRef,
    masterFilterRef,
    masterHighpassRef,
  masterLimiterRef,
  masterClipperRef,
  masterLookaheadLimiterRef,
  teeGainRef,
    sourcesRef,
    gainsRef,
    analyzersRef,
    delaysRef,
    connectedElsRef,
    elsRef,
    durationsRef,
    lastNonZeroVolRef,
    // mutable fields below will be attached to this object at runtime elsewhere:
    masterGraph: null as MasterGraph | null,
    stems: [] as StemGraph[],
    audioEls: {} as Record<string, HTMLAudioElement>,
  }), []);
}

export type EngineRefs = ReturnType<typeof useEngineRefs>;

// Thoroughly dispose per-stem nodes to avoid leaks/freezes during scope swaps
export function disposeStems(
  ctx: AudioContext,
  dicts: {
    sourcesRef?: React.MutableRefObject<Record<string, AudioNode | undefined>>;
    gainsRef: React.MutableRefObject<Record<string, GainNode>>;
    analyzersRef: React.MutableRefObject<Record<string, AnalyserNode>>;
    delaysRef: React.MutableRefObject<Record<string, DelayNode>>;
  }
) {
  const safeDisc = (n?: AudioNode | null) => { try { n?.disconnect(); } catch {} };
  try {
    Object.values(dicts.gainsRef.current || {}).forEach(safeDisc);
    Object.values(dicts.analyzersRef.current || {}).forEach(safeDisc);
    Object.values(dicts.delaysRef.current || {}).forEach(safeDisc);
    Object.values(dicts.sourcesRef?.current || {}).forEach(safeDisc);
  } catch {}
  try { dicts.gainsRef.current = {} as any; } catch {}
  try { dicts.analyzersRef.current = {} as any; } catch {}
  try { dicts.delaysRef.current = {} as any; } catch {}
  if (dicts.sourcesRef) { try { dicts.sourcesRef.current = {} as any; } catch {} }
}
